import * as React from 'react';
import i18n from 'i18next';
import _set from 'lodash/set';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

// components
import { Button, Flexbox, IconButton } from 'components/spaceKit';
import ActionColorSwatchGroup from 'components/_UI/Forms/Actions/ActionColorSwatchGroup';
import LabeledFormElement from 'components/_UI/Forms/LabeledFormElement/LabeledFormElement';

// constants
import { BrandColorsArray } from '@sprinklr/display-builder/stores/PanelTemplatesStore/PanelTemplatesTheme/constants';

// utils
import { updateWidgetOptions } from 'utils/Widget/WidgetUtils';
import {
    ColorTag,
    ColorTagConditionMetric,
} from '@sprinklr/display-builder/widgets/utils/getColorTag';
import { getNewConditionValues } from './utils/getValues';
import { getMetricDesignOptionsKey } from '../metricDesignTunables/utils/getMetricDesignOptionsKey';
import { Condition } from './condition/Condition';

//types
import { ColorTagTunablesProps } from './types';

// color tag conversion from pre April 2023 to current happens in DataStore -> WidgetImpl mapper
// https://sprinklr.atlassian.net/browse/DISPLAY-4840
function ColorTagTunables({ widget, projection, rootOption = false }: ColorTagTunablesProps) {
    const metricKey = getMetricDesignOptionsKey(
        projection?.measurementName,
        projection?.aggregateFunction
    );
    const options = rootOption
        ? widget?.options
        : widget?.options?.metricDesignOptions?.[metricKey];
    const { colorTags }: { colorTags: ColorTag[] } = options || ({} as { colorTags: ColorTag[] });

    const addRule = action(() => {
        const lastRule = colorTags?.at(-1);
        const lastCondition = lastRule?.conditions?.at(-1);

        const newTag = observable<ColorTag>({
            color:
                BrandColorsArray.slice()
                    .reverse()
                    .find(color => !colorTags?.find(colorTag => colorTag.color === color)) ||
                lastRule?.color,
            conditions: [
                {
                    type: 'METRIC',
                    name: metricKey,
                    ...getNewConditionValues(lastCondition),
                } as ColorTagConditionMetric,
            ],
        });

        if (colorTags) {
            colorTags.push(newTag as any);
        } else {
            const newColorTags = observable([newTag]);
            if (rootOption) {
                updateWidgetOptions(widget, `colorTags`, newColorTags);
            }
            updateWidgetOptions(widget, `metricDesignOptions.${metricKey}.colorTags`, newColorTags);
        }
    });

    const addCondition = action((index: number) => {
        const lastCondition = colorTags[index].conditions.at(-1);

        const newCondition = observable({
            type: 'METRIC',
            name: metricKey,
            ...getNewConditionValues(lastCondition),
        });
        colorTags[index].conditions.push(newCondition as any);
    });

    const removeRule = (index: number) => {
        runInAction(() => colorTags.splice(index, 1));
    };

    return (
        <>
            <LabeledFormElement label={i18n.t('Color Tags')} gap='xl'>
                {colorTags?.map((tag, index) => {
                    return (
                        <Flexbox key={index} middle vertical gap='ml'>
                            <Flexbox left middle gap='ml'>
                                <ActionColorSwatchGroup value='color' option={tag} />
                                <IconButton
                                    icon='icon-minus'
                                    onClick={() => removeRule(index)}
                                    data-testid='icon-button-remove-color-tag-rule'
                                />
                            </Flexbox>

                            {tag.conditions?.map((condition, conditionIndex) => {
                                return (
                                    <Condition
                                        key={conditionIndex}
                                        condition={condition}
                                        tag={tag}
                                        index={conditionIndex}
                                        widget={widget}
                                    />
                                );
                            })}

                            <Button type='secondary' onClick={() => addCondition(index)}>
                                {i18n.t('Add Condition')}
                            </Button>
                        </Flexbox>
                    );
                })}

                <IconButton
                    icon='icon-plus'
                    onClick={addRule}
                    data-testid='icon-button-add-color-tag'
                />
            </LabeledFormElement>
        </>
    );
}

export default observer(ColorTagTunables);
